/**
 * Bridge helpers between the app's `WheelItem` (the live stock/prize shape the
 * public wizard hands every game) and the vendored canvases' `WheelSegment`.
 *
 * The single source of winner selection stays `pickPrize` — these helpers only
 * shape the data the canvas draws (prize names, ES translation, image/logo
 * URLs, and the supporting pool of faces/pockets the winner is placed into).
 */
import { resolveImageUrl } from '@/lib/images/resolveImageUrl';
import type { WheelItem } from '../pickPrize';
import type { WheelSegment } from '../_vendor/types';

/** Narrow the survey locale down to the two the canvases render. */
export function gameLang(lang?: 'en' | 'es' | string): 'en' | 'es' {
  return lang === 'es' ? 'es' : 'en';
}

/** Map one live prize to the canvas segment shape (URLs resolved). */
export function toSegment(item: WheelItem): WheelSegment {
  return {
    id: item.id,
    name: item.name,
    logo: item.logo ? resolveImageUrl(item.logo) : undefined,
    image: item.image ? resolveImageUrl(item.image) : undefined,
    rating: item.rating,
  };
}

/**
 * Build the supporting pool a multi-slot game shows (soccer pockets, pirinola
 * faces). The pre-chosen `winner` is always included; the pool is padded to
 * exactly `n` by cycling the available prizes so the surface is never short.
 */
export function buildPool(valid: WheelItem[], winner: WheelItem, n: number): WheelSegment[] {
  const winnerSeg = toSegment(winner);
  const others = valid.filter((v) => v.id !== winner.id).map(toSegment);
  const base = [winnerSeg, ...others];
  if (base.length >= n) return base.slice(0, n);
  const out = [...base];
  for (let i = 0; out.length < n; i++) out.push(base[i % base.length]);
  return out;
}
